import clsx from 'clsx';
import type { CSSProperties, ComponentProps, HTMLAttributes } from 'react';

import { SmartList } from '@/components/smart-list';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';

import { column, toGridTemplateColumns, type ColumnDef } from './column';

import styles from './virtual-table.module.scss';

export { column };
export type { ColumnDef };

const SKELETON_ROW_COUNT = 12;
const LOAD_MORE_SKELETON_ROW_COUNT = 3;

type SmartListProps<T extends { id: string }> = ComponentProps<typeof SmartList<T>>;

export const VirtualTable = <T extends { id: string }>(props: VirtualTableProps<T>) => {
  const {
    items,
    columns,
    overscan,
    resetKey,
    className,
    getItemKey,
    estimateSize,
    endReachRows,
    loading = false,
    selections = [],
    onLastItemVisible,
    loadingMore = false,
    disableRowStates = true,
    rowProps = emptyRowProps,
  } = props;

  const gridTemplateColumns = toGridTemplateColumns(columns);
  const showSkeletons = loading && items.length === 0;

  return (
    <div className={clsx(styles.virtualTable, className)} style={{ ['--table-columns']: gridTemplateColumns } as CSSProperties}>
      <div className={styles.header} role="row">
        {columns.map((col) => (
          <div key={col.id} className={styles.headerCell} role="columnheader">
            {col.header}
          </div>
        ))}
      </div>
      <div className={styles.body}>
        {showSkeletons ? (
          <div className={styles.skeletonBody}>
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <div
                key={index}
                className={styles.row}
                role="row"
                data-testid="virtual-table-skeleton-row"
                style={{ minHeight: estimateSize }}
              >
                {columns.map((col) => (
                  <div key={col.id} className={styles.cell} role="cell">
                    {col.skeleton?.() ?? <Skeleton height={6} borderRadius={4} width={72} />}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className={styles.listHolder}>
              <SmartList<T>
                data={items}
                loading={loading}
                resetKey={resetKey}
                selections={selections}
                defaultSize={estimateSize}
                overscan={overscan}
                getItemKey={getItemKey}
                onLastItemVisible={onLastItemVisible}
                endReachRows={endReachRows}
                rowProps={rowProps}
                customDividers={false}
                disableRowStates={disableRowStates}
                rowRenderer={(item) => (
                  <div className={clsx(styles.row, 'virtual-table-row')} role="row">
                    {columns.map((col) => (
                      <div key={col.id} className={styles.cell} role="cell">
                        {col.cell(item)}
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
            {loadingMore && (
              <div className={styles.loadMore}>
                {Array.from({ length: LOAD_MORE_SKELETON_ROW_COUNT }, (_, index) => (
                  <div
                    key={index}
                    className={styles.row}
                    role="row"
                    data-testid="virtual-table-load-more-row"
                    style={{ minHeight: estimateSize }}
                  >
                    {columns.map((col) => (
                      <div key={col.id} className={styles.cell} role="cell">
                        {col.skeleton?.() ?? <Skeleton height={6} borderRadius={4} width={72} />}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const emptyRowProps = <T,>(_row: T, _selected: boolean): HTMLAttributes<HTMLUListElement> => ({});

export type VirtualTableProps<T extends { id: string }> = {
  columns: ColumnDef<T>[];
  items: T[];
  estimateSize: number;
  loading?: boolean;
  loadingMore?: boolean;
  resetKey?: string;
  className?: string;
  overscan?: number;
  getItemKey?: SmartListProps<T>['getItemKey'];
  onLastItemVisible?: SmartListProps<T>['onLastItemVisible'];
  endReachRows?: SmartListProps<T>['endReachRows'];
  rowProps?: SmartListProps<T>['rowProps'];
  selections?: SmartListProps<T>['selections'];
  disableRowStates?: boolean;
};
